import React, { useEffect, useState } from 'react';
import { Modal, Button, Select, Checkbox, Tree, Divider, message } from 'antd';
import { UtilisateurService } from '../../Services/UtilisateurService';

const gestionUsersService = new UtilisateurService();

const GestionPrivilegesModal = ({ isVisible, onClose }) => {
  const [selectedRole, setSelectedRole] = useState('role_1');
  const [userRoles, setUserRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [checkAll, setCheckAll] = useState(false);

  // Transforme les privilèges en treeData pour le composant Tree
  // ...existing code...

  function transformPrivilegesToTreeData(privilegesJson) {
    const groupedByMenu = {};

    privilegesJson.forEach(privilege => {
      const { menu, ...rest } = privilege;
      if (!groupedByMenu[menu]) {
        groupedByMenu[menu] = {
          title: menu,
          key: `menu-${menu}`,
          children: []
        };
      }
      groupedByMenu[menu].children.push({
        key: `child-${rest.fonc_code}`,
        valeur: rest.valeur,
        fonc_code: rest.fonc_code,
        fonctionnalite: rest.fonctionnalite,
        menu: menu
      });
    });

    return Object.values(groupedByMenu);
  }

  // ...rest of the code remains unchanged...

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await gestionUsersService.getUserRoles();
        setUserRoles(roles);
      } catch (error) {
        console.error('Erreur lors de la récupération des rôles :', error);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchPrivileges = async () => {
      if (selectedRole) {
        try {
          const privileges = await gestionUsersService.getUserPrivileges(selectedRole);
          setPrivileges(transformPrivilegesToTreeData(privileges));
          setCheckAll(privileges.every(p => p.valeur));
        } catch (error) {
          console.error('Erreur lors de la récupération des privilèges :', error);
        }
      }
    };
    fetchPrivileges();
  }, [selectedRole]);

  // Modifie la valeur du checkbox ciblé
  const handleCheckboxChange = (menu, fonc_code) => {
    setPrivileges(prev =>
      prev.map(menuItem =>
        menuItem.title === menu
          ? {
            ...menuItem,
            children: menuItem.children.map(child =>
              child.fonc_code === fonc_code
                ? { ...child, valeur: !child.valeur }
                : child
            )
          }
          : menuItem
      )
    );
    // Met à jour l'état global "tout coché"
    setCheckAll(
      privileges
        .flatMap(menu => menu.children)
        .filter(child => child.fonc_code !== fonc_code)
        .every(child => child.valeur)
      && !privileges
        .flatMap(menu => menu.children)
        .find(child => child.fonc_code === fonc_code)?.valeur
    );
  };

  // Cocher/Décocher toutes les cases enfants
  const handleCheckAll = (e) => {
    const checked = e.target.checked;
    setCheckAll(checked);
    setPrivileges(prev =>
      prev.map(menuItem => ({
        ...menuItem,
        children: menuItem.children.map(child => ({
          ...child,
          valeur: checked
        }))
      }))
    );
  };

  const handleChangeRole = value => {
    setSelectedRole(value);
  };

  const handleClose = (e) => {
    if (e) e.preventDefault();
    if (onClose) onClose();
  };

  function extractPrivilegesChildren(privileges, selectedRole) {
    // privileges : liste structurée en treeData (menus avec children)
    // Retourne une liste plate des enfants avec les propriétés demandées
    return privileges.flatMap(menuItem =>
      menuItem.children.map(child => ({
        role_code: selectedRole,
        fonc_code: child.fonc_code,
        valeur: child.valeur
      }))
    );
  }

  const privilegesToSave = extractPrivilegesChildren(privileges, selectedRole);

  const handleSaveRole = () => {
    // Logique pour sauvegarder les modifications de rôle
    console.log('Rôle sauvegardé :', selectedRole);
    // console.log('Privilèges associés :', privilegesToSave);
    gestionUsersService.updateUserPrivileges(privilegesToSave)
      .then(response => {
        console.log('Privilèges mis à jour avec succès :', response);
      })
      .catch(error => {
        console.error('Erreur lors de la mise à jour des privilèges :', error);
      });
    handleClose(new Event('save'));
  };

  if (!isVisible) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 18 }}>Gestion des privilèges</span>
        </div>
      }
      open={isVisible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} style={{ borderRadius: 6 }}>Annuler</Button>,
        <Button key="save" type="primary" onClick={() => {
          handleSaveRole();
          message.success({ content: 'Privilèges mis à jour avec succès', duration: 2 });
        }} style={{ borderRadius: 6 }}>Sauvegarder</Button>,
      ]}
      centered
      bodyStyle={{ background: "#f8fafd", borderRadius: 12, padding: 24 }}
      style={{ borderRadius: 12, minWidth: 400 }}
    >
      {/* Ajout du label pour le Select */}
      <div style={{ marginBottom: 4, fontWeight: 500 }}>Choisissez un rôle :</div>
      <Select
        value={selectedRole}
        onChange={handleChangeRole}
        placeholder="Choisissez un rôle"
        style={{ width: '100%', marginBottom: 16, borderRadius: 6 }}
      >
        {userRoles.map(role => (
          <Select.Option key={role.role_code} value={role.role_code}>
            {role.role_name}
          </Select.Option>
        ))}
      </Select>
      <Divider />
      {/* Ajout du texte avant la case à cocher */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 500 }}>
          Privilèges du rôle : {
            userRoles.find(r => r.role_code === selectedRole)?.role_name || ""
          }
        </span>
        <Checkbox checked={checkAll} onChange={handleCheckAll}>
          Cocher / Décocher tout
        </Checkbox>
      </div>
      <Tree
        treeData={privileges.map(menuItem => ({
          ...menuItem,
          children: menuItem.children.map(child => ({
            ...child,
            title: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  checked={child.valeur}
                  onChange={() => handleCheckboxChange(child.menu, child.fonc_code)}
                />
                <span>{child.fonctionnalite}</span>
              </div>
            )
          }))
        }))}
        selectable={false}
        style={{ background: "#fff", borderRadius: 8, padding: 8 }}
      />
    </Modal>
  );
};

export default GestionPrivilegesModal;